const ACCESS_TOKEN_KEY = 'vectria_auth_token';
const REFRESH_TOKEN_KEY = 'vectria_auth_refresh_token';
const USER_KEY = 'vectria_auth_user';

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface RefreshedSession {
  token: string;
  refreshToken: string;
  user?: unknown;
}

export interface AuthenticatedRequestOptions {
  accessTokenFallback?: string | null;
  fetchImpl?: FetchLike;
  onSessionUpdated?: (session: RefreshedSession) => void;
  onSessionExpired?: () => void;
}

export class AuthSessionExpiredError extends Error {
  constructor(message = 'Your session has expired. Sign in again.') {
    super(message);
    this.name = 'AuthSessionExpiredError';
  }
}

let refreshInFlight: Promise<RefreshedSession> | null = null;

const clearStorage = (): void => {
  for (const storage of [localStorage, sessionStorage]) {
    storage.removeItem(ACCESS_TOKEN_KEY);
    storage.removeItem(REFRESH_TOKEN_KEY);
    storage.removeItem(USER_KEY);
  }
};

const storedSession = () => {
  const localRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  const storage = localRefreshToken ? localStorage : sessionStorage;
  return {
    storage,
    otherStorage: storage === localStorage ? sessionStorage : localStorage,
    refreshToken: localRefreshToken || sessionStorage.getItem(REFRESH_TOKEN_KEY),
  };
};

export const refreshStoredSession = async (
  apiBase: string,
  fetchImpl: FetchLike = fetch,
): Promise<RefreshedSession> => {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const { storage, otherStorage, refreshToken } = storedSession();
      if (!refreshToken) {
        clearStorage();
        throw new AuthSessionExpiredError();
      }

      let response: Response;
      try {
        response = await fetchImpl(`${apiBase}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
          signal: AbortSignal.timeout(15000),
        });
      } catch (error) {
        throw new Error(
          error instanceof Error && error.name === 'TimeoutError'
            ? 'Session renewal timed out. Please try again.'
            : 'Could not renew the session because the server is unreachable. Please try again.',
        );
      }

      const data = await response.json().catch(() => ({}));
      if (response.status === 401 || response.status === 403) {
        clearStorage();
        throw new AuthSessionExpiredError();
      }
      if (!response.ok) {
        throw new Error(`Could not renew the session (${response.status}). Please try again.`);
      }
      if (!data.token || !data.refresh_token) {
        throw new Error('The server returned an incomplete session renewal response. Please try again.');
      }

      storage.setItem(ACCESS_TOKEN_KEY, data.token);
      storage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
      otherStorage.removeItem(ACCESS_TOKEN_KEY);
      otherStorage.removeItem(REFRESH_TOKEN_KEY);
      if (data.user) {
        storage.setItem(USER_KEY, JSON.stringify(data.user));
        otherStorage.removeItem(USER_KEY);
      }
      return { token: data.token, refreshToken: data.refresh_token, user: data.user };
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

export const authenticatedRequest = async (
  apiBase: string,
  path: string,
  requestInit: RequestInit = {},
  options: AuthenticatedRequestOptions = {},
): Promise<Response> => {
  const fetchImpl = options.fetchImpl || fetch;
  const token = localStorage.getItem(ACCESS_TOKEN_KEY)
    || sessionStorage.getItem(ACCESS_TOKEN_KEY)
    || options.accessTokenFallback;
  if (!token) {
    options.onSessionExpired?.();
    throw new AuthSessionExpiredError('Sign in to continue.');
  }

  const headers = new Headers(requestInit.headers || {});
  headers.set('Authorization', `Bearer ${token}`);
  const response = await fetchImpl(`${apiBase}${path}`, { ...requestInit, headers });
  if (response.status !== 401) return response;

  try {
    const session = await refreshStoredSession(apiBase, fetchImpl);
    options.onSessionUpdated?.(session);
    headers.set('Authorization', `Bearer ${session.token}`);
    const retried = await fetchImpl(`${apiBase}${path}`, { ...requestInit, headers });
    if (retried.status === 401) {
      clearStorage();
      options.onSessionExpired?.();
      throw new AuthSessionExpiredError();
    }
    return retried;
  } catch (error) {
    if (error instanceof AuthSessionExpiredError) options.onSessionExpired?.();
    throw error;
  }
};
